import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { CompanyApi } from '../../../../../api/companyApi';
import { errorToast, getLocalStorageItem, isCopanySubAdmin } from '../../../../../utils/helper';
import Loader from '../../../../../component/common/Loader';
import SearchInput from '../../../../../component/common/Input/SearchInput';
import Topbar from '../../../../PulseSurvey/Topbar';
import Breadcrumb from '../../../../../component/common/Breadcrumb';
import { StandardButton, EmptyState } from '../../../../../component/common/StandardUI';
import roadMapIcon from '../../../../../assets/svg/icons/Timeline-Roadmap-Horizontal--Streamline-Flex.svg';

const PAGE_SIZE = 10;

// `published` is the model's active state (see RoadMap status enum).
// Sub admins get a read-only view and never see other admins' drafts.
const STATUS_TABS = [
  { key: 'published', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'draft', label: 'Draft', superAdminOnly: true }
];

const DEFAULT_STATUS = STATUS_TABS[0].key;

const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => `${new Date().getFullYear() - i}`);

const RoadMapCard = ({ roadMap, onClick }) => (
  <div className="relative rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-5 hover:border-shoorah-btn dark:hover:border-shoorah-btnDark transition-colors group">
    <button type="button" onClick={onClick} className="text-left w-full">
      <div className="flex items-start gap-3">
        <img src={roadMapIcon} alt="" className="w-5 h-5 shrink-0 mt-0.5 dark:invert" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-700 dark:text-gray-200">{roadMap.title}</p>
          <p className="text-sm text-gray-400 line-clamp-2 mt-1">{roadMap.description}</p>
          <p className="text-xs text-gray-400 mt-3">
            Date created:{' '}
            {roadMap.createdAt ? moment(roadMap.createdAt).format('Do MMMM YYYY') : '—'}
          </p>
        </div>
      </div>
    </button>
  </div>
);

const RoadMaps = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData'));
  const slug = userData?.slug;
  const isSubAdmin = isCopanySubAdmin();
  const pages = [
    { name: 'People plan', href: `/${slug}/people-plan`, current: false },
    { name: 'Road Maps', href: `/${slug}/people-plan/roadmaps`, current: true }
  ];

  const [list, setList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedPages, setLoadedPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [year, setYear] = useState('');

  // "View more" appends the next page instead of replacing the list.
  const fetchList = useCallback(
    async (page = 1, filters = {}, append = false) => {
      try {
        if (append) setLoadingMore(true);
        else setLoader(true);
        const response = await CompanyApi.getAllRoadMaps(page, PAGE_SIZE, filters);
        if (response?.data?.meta?.code === 1) {
          const data = response?.data?.data || [];
          setList((prev) => (append ? [...prev, ...data] : data));
          setTotalCount(response?.data?.meta?.total || 0);
          setLoadedPages(page);
        } else if (!append) {
          setList([]);
          setTotalCount(0);
        }
      } catch (error) {
        errorToast(error.message || 'Failed to load road maps');
      } finally {
        setLoader(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchList(1, { status, year, title: searchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasMore = list.length < totalCount;
  const activeTabLabel = STATUS_TABS.find((tab) => tab.key === status)?.label || '';
  const visibleTabs = STATUS_TABS.filter((tab) => !tab.superAdminOnly || !isSubAdmin);

  const handleViewMore = () => fetchList(loadedPages + 1, { status, year, title: searchTerm }, true);

  const handleStatusChange = (nextStatus) => {
    if (nextStatus === status) return;
    setStatus(nextStatus);
    fetchList(1, { status: nextStatus, year, title: searchTerm });
  };

  const handleYearChange = (nextYear) => {
    setYear(nextYear);
    fetchList(1, { status, year: nextYear, title: searchTerm });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchList(1, { status, year, title: value });
  };

  return (
    <div className="relative lg:mb-[45px]">
      {loader && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>People plan - Road Maps | Shoorah Admin</title>
      </Helmet>
      <Topbar title="People plan" infoBtn={false} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="mt-6">
        <div className="rounded-input border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-6">
          <div>
            <h2 className="text-base font-medium text-gray-700 dark:text-gray-200">Road Maps</h2>
            <p className="text-sm text-gray-400">
              {isSubAdmin
                ? 'Active and completed road maps across your company.'
                : 'Outline your key internal goals and milestones across each period.'}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-5 mb-5">
            <div className="flex items-center gap-6">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => handleStatusChange(tab.key)}
                  className={`border-b-2 pb-1.5 text-sm font-medium transition-colors ${status === tab.key
                      ? 'border-shoorah-btn text-shoorah-btn dark:border-shoorah-btnDark dark:text-white'
                      : 'border-transparent text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:ml-auto">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search road maps"
                inputClassName="!rounded-btn !h-[40px] focus:!border-shoorah-btn focus:!ring-0 dark:focus:!border-shoorah-btnDark"
              />

              <Menu as="div" className="relative shrink-0">
                <Menu.Button className="flex items-center gap-2 rounded-btn border-[1.5px] border-shoorah-borderGray dark:border-gray-600 h-[40px] px-4 text-sm text-gray-600 dark:text-white">
                  <FunnelIcon className="w-4 h-4" />
                  Filter
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-56 rounded-input bg-white dark:bg-shoorah-darkBgTabColor border border-shoorah-borderGray dark:border-shoorah-darkBorder shadow-lg p-3">
                    <p className="text-xs text-gray-400 mb-1">Year</p>
                    <select
                      value={year}
                      onChange={(e) => handleYearChange(e.target.value)}
                      className="w-full rounded-input border border-gray-300 dark:border-gray-600 dark:bg-shoorah-darkBgColor dark:text-white text-sm px-2 py-1 focus:outline-none"
                    >
                      <option value="">All years</option>
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </Menu.Items>
                </Transition>
              </Menu>

              {!isSubAdmin && (
                <StandardButton
                  text="New Road Map"
                  onClick={() => navigate(`/${slug}/people-plan/roadmaps/add`)}
                />
              )}
            </div>
          </div>

          {!loader && list.length === 0 && (
            <EmptyState
              size="lg"
              title={`No ${activeTabLabel.toLowerCase()} road maps`}
              description={
                searchTerm || year
                  ? 'No road maps match the selected filters.'
                  : 'Create a road map to outline your key internal goals and milestones.'
              }
              className="border-none"
            />
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {list.map((roadMap) => (
              <RoadMapCard
                key={roadMap._id}
                roadMap={roadMap}
                onClick={() => navigate(`/${slug}/people-plan/roadmaps/view/${roadMap._id}`)}
              />
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={handleViewMore}
              disabled={loadingMore}
              className="mt-5 text-sm font-medium text-shoorah-primary hover:underline disabled:opacity-60"
            >
              {loadingMore ? 'Loading...' : 'View more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoadMaps;
